import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useCardMotion } from "@/hooks/use-card-motion";

export function Container({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("container-page", className)}>{children}</div>;
}

export function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {children}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  align = "left",
}: {
  eyebrow?: string;
  title: ReactNode;
  /** Kept for backwards compatibility; intentionally not rendered. */
  sub?: ReactNode;
  align?: "left" | "center";
}) {
  return (
    <div className={cn("max-w-3xl", align === "center" && "mx-auto text-center")}>
      {eyebrow && <SectionEyebrow>{eyebrow}</SectionEyebrow>}
      <h2 className="mt-2 text-xl font-semibold tracking-tight text-foreground md:text-2xl lg:text-3xl">
        {title}
      </h2>
    </div>
  );
}

export function GlassCard({
  children,
  className,
  hover = true,
  animateIndex = 0,
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  /** Stagger index when card animations are enabled (grid position). */
  animateIndex?: number;
}) {
  const motion = useCardMotion(animateIndex);

  return (
    <div
      ref={motion.ref}
      style={motion.style}
      className={cn(
        "relative rounded-xl bg-card p-6 shadow-[var(--shadow-card)] transition duration-300",
        hover && "hover:-translate-y-1 hover:shadow-[var(--shadow-soft)]",
        motion.className,
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Card shell with optional fade-in-up (respects admin site setting). */
export function CardMotion({
  animateIndex = 0,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { animateIndex?: number }) {
  const motion = useCardMotion(animateIndex);

  return (
    <div
      ref={motion.ref}
      style={motion.style}
      className={cn(motion.className, className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function CTALink({
  to,
  params,
  children,
  variant = "primary",
}: {
  to: string;
  params?: Record<string, string>;
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost";
}) {
  const base = "inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-base font-semibold transition";
  const styles = {
    primary: "border border-gold bg-transparent text-gold hover:bg-gold/10",
    secondary: "border border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground",
    ghost: "text-primary hover:bg-primary/8 rounded-md",
  } as const;
  return (
    <Link to={to as never} params={params as never} className={cn(base, styles[variant])}>
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

export function AuroraBackdrop() {
  return (
    <div className="aurora">
      <div className="absolute -left-32 -top-32 h-[480px] w-[480px] rounded-full bg-primary/15 blur-3xl" />
      <div className="absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-accent/15 blur-3xl" />
      <div className="absolute left-1/3 top-1/2 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
}: {
  eyebrow?: string;
  title: ReactNode;
  /** Kept for backwards compatibility; intentionally not rendered. */
  sub?: ReactNode;
}) {
  return (
    <section className="relative isolate overflow-hidden bg-white">
      <Container>
        <div className="flex min-h-[32vh] flex-col justify-end py-10 md:min-h-[40vh] md:py-14">
          {eyebrow ? (
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-gold">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="mt-3 max-w-3xl text-3xl font-semibold leading-[1.15] tracking-tight text-primary md:text-4xl lg:text-5xl">
            {title}
          </h1>
        </div>
      </Container>
    </section>
  );
}
